/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import * as agGrid from 'ag-grid-community'; //NOSONAR
import {ColDef, GridApi, GridOptions, GridReadyEvent} from 'ag-grid-community'; //NOSONAR
import {getIxTheme} from '@siemens/ix-aggrid';
import {switchMap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {AccessRequestManagementService} from '../../../../services/access-request-management.service';
import {UserService} from '../../../../services/user.service';
import {GeneralDataHandlingService} from '../../../../services/general.data-handling.service';
import {
  AccessRequest,
  AccessRequestStatusQuery,
} from '../../../../models/AccessRequest';

@Component({
  selector: 'app-list-access-requests',
  templateUrl: './list-access-requests.component.html',
  styleUrl: './list-access-requests.component.scss',
  standalone: false,
})
export class ListAccessRequestsComponent implements OnInit {
  private readonly accessRequestManagementService = inject(
    AccessRequestManagementService,
  );
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  gridApi: GridApi | null = null;
  loading = true;
  accessRequests: AccessRequest[] = [];

  gridOptions: GridOptions = {
    theme: getIxTheme(agGrid),
    rowSelection: 'single',
  };

  columnDefs: ColDef[] = [
    {
      field: 'firstName',
      headerValueGetter: () => this.translateService.instant('um.first_name'),
      sortable: true,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'lastName',
      headerValueGetter: () => this.translateService.instant('um.last_name'),
      sortable: true,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'email',
      headerValueGetter: () => this.translateService.instant('um.email'),
      sortable: true,
      minWidth: 180,
      flex: 2,
    },
    {
      field: 'organization',
      headerValueGetter: () =>
        this.translateService.instant(
          'battery_pass.public.request_access_form.label_organization',
        ),
      sortable: true,
      minWidth: 150,
      flex: 2,
    },
    {
      field: 'roles',
      headerValueGetter: () =>
        this.translateService.instant(
          'battery_pass.public.request_access_form.label_desired_roles',
        ),
      sortable: false,
      minWidth: 150,
      flex: 2,
      valueGetter: (params) =>
        Array.isArray(params.data?.roles)
          ? params.data.roles
              .map((role: string) =>
                this.translateService.instant(
                  'um.roles.' +
                    GeneralDataHandlingService.returnRoleLabel(role),
                ),
              )
              .join(', ')
          : '',
    },
    {
      field: 'createdAt',
      headerValueGetter: () =>
        this.translateService.instant('um.access_requests.created_at'),
      sortable: true,
      minWidth: 150,
      flex: 1,
      valueFormatter: (params) =>
        params.value
          ? new Date(params.value as string).toLocaleDateString()
          : '',
    },
  ];

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  ngOnInit(): void {
    this.loading = true;
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gridApi?.refreshHeader();
        this.gridApi?.refreshCells({force: true});
      });

    this.userService.userDataChange
      .pipe(
        switchMap((user) => {
          const organizationId = user?.organization.id ?? '';
          return this.accessRequestManagementService.loadAccessRequests$(
            organizationId,
            {status: AccessRequestStatusQuery.VERIFIED},
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      // Load errors fall back to an empty list; no toast is shown (Pattern C).
      .subscribe({
        next: (response) => {
          this.accessRequests = response.data;
          this.loading = false;
        },
        error: () => {
          this.accessRequests = [];
          this.loading = false;
        },
      });
  }

  async onRowClicked(event: {data: AccessRequest}) {
    await this.router.navigate([`app/edit-access-request/${event.data.id}`]);
  }
}
